/**
 * LoggerService is a singleton wrapper that bridges the connection to the logger,
 * allowing you to call logging functionality from non-component code (e.g., services, utilities).
 * This enables consistent logging throughout the application, even outside of React or Angular components.
 */
import { LoggerOptions } from './LoggerProvider';

class LoggerService {
  private static instance: LoggerService | null = null;
  private loggerFunction: ((opts: LoggerOptions) => string) | null = null;
  private pendingLogs: LoggerOptions[] = []; // Store logs that happen before logger is set

  public static getInstance(): LoggerService {
    if (!LoggerService.instance) {
      LoggerService.instance = new LoggerService();
    }
    return LoggerService.instance;
  }

  public setLogger(logger: (opts: LoggerOptions) => string) {
    this.loggerFunction = logger;

    // Process any pending logs
    if (this.pendingLogs.length > 0) {
      const logsToProcess = [...this.pendingLogs];
      this.pendingLogs = [];

      // Process each pending log
      logsToProcess.forEach(logOpts => {
        this.log(logOpts);
      });
    }
  }

  public log(opts: LoggerOptions): string | undefined {
    if (this.loggerFunction) {
      return this.loggerFunction(opts);
    } else {
      // Store the log to process later when logger is set
      this.pendingLogs.push(opts);

      // Fallback to console
      console[opts.logLevel === 'error' ? 'error' : 'log'](opts.message, {
        context: opts.context,
        error: opts.error,
      });

      return undefined; // Can't return a log ID without the actual logger
    }
  }
}

export const loggerService = LoggerService.getInstance();
